import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PromptForm, PromptsList } from '@web/app/features/prompts/components';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { Loader2 } from 'lucide-react';

import { useMyPrompts } from '../hooks';

export const PromptsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        promptId,
        isCreateMode,
        searchQuery,
        page,
        limit,
        stereo,
        promptsData,
        isPromptsLoading,
        selectedPrompt,
        isPromptLoading,
        paginationRange,
        isFormVisible,
        formMode,
        handlePromptSelect,
        handleCreateNew,
        handleFormSuccess,
        handlePageChange,
        handleSearchChange,
        handleStereoChange,
    } = useMyPrompts();

    const handlePromptDeleted = (deletedPromptId: string) => {
        // If the deleted prompt is the currently selected one, navigate back to prompts list
        const isCurrentlySelectedPrompt = promptId === deletedPromptId;
        if (isCurrentlySelectedPrompt) {
            navigate(-1);
        }
    };

    return (
        <div className="flex-1 bg-background min-h-0 w-full flex flex-col">
            <div className="border-b border-detail-line text-lg font-semibold font-display py-3 px-8 max-md:py-4 max-md:px-4 max-md:bg-transparent max-md:border-none">
                {t('menu.ai.prompt')}
            </div>

            <PageContainer
                noPadding
                className="flex-1 py-8 px-8 flex justify-center max-md:px-4 max-md:pt-0 animate-enter"
            >
                <div className="flex-1 grid grid-cols-[1fr_2fr] max-md:grid-cols-1 gap-6 w-full">
                    <PromptsList
                        prompts={promptsData?.data || []}
                        isLoading={isPromptsLoading}
                        selectedPromptId={promptId || null}
                        searchQuery={searchQuery}
                        onPromptSelect={handlePromptSelect}
                        onCreateNew={handleCreateNew}
                        onSearchChange={handleSearchChange}
                        currentPage={page}
                        totalCount={promptsData?.total || 0}
                        pageSize={limit}
                        paginationRange={paginationRange}
                        onPageChange={handlePageChange}
                        selectedFilter={stereo}
                        onFilterChange={handleStereoChange}
                        onPromptDeleted={handlePromptDeleted}
                    />

                    {isFormVisible ? (
                        isPromptLoading && promptId ? (
                            <div className="flex items-center gap-2 w-full h-full justify-center">
                                <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                        ) : (
                            <PromptForm
                                key={`${formMode}-${promptId || 'new'}`}
                                mode={formMode as 'create' | 'edit'}
                                promptId={promptId || null}
                                promptData={selectedPrompt || null}
                                onFormSuccess={handleFormSuccess}
                            />
                        )
                    ) : (
                        <div className="h-full min-h-[302px] bg-card shadow-custom border border-chatbot-line rounded-2xl flex items-center justify-center">
                            <div className="text-center text-text-500 px-4">
                                <div className="text-lg font-medium mb-2">{t('prompt.page.emptyState.title')}</div>
                                <div className="text-sm">{t('prompt.page.emptyState.description')}</div>
                            </div>
                        </div>
                    )}
                </div>
            </PageContainer>
        </div>
    );
};
